<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="pl-question">
        <span class="pl-bar pl-bar--xl"></span>
        <span class="pl-bar pl-bar--md"></span>
      </div>
      <div class="pl-results">
        <div
          v-for="(opt, i) in options"
          :key="opt.label"
          class="pl-row"
          :style="{ '--row-i': i }"
        >
          <span class="pl-row-label">{{ opt.label }}</span>
          <div class="pl-track">
            <div
              class="pl-fill"
              :style="{ '--row-pct': opt.pct + '%', '--row-i': i }"
            ></div>
          </div>
          <span class="pl-pct">{{ opt.pct }}%</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="pl-phone-q"></span>
      <button
        v-for="(opt, i) in options"
        :key="opt.label"
        type="button"
        class="pl-pill"
        :class="{ '--tapped': i === tapIndex }"
        :style="{ '--row-i': i }"
        tabindex="-1"
      >
        <span class="pl-pill-text">{{ opt.label }}</span>
        <span v-if="i === tapIndex" class="material-symbols-rounded pl-pill-check">check</span>
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

type Option = { label: string; pct: number }

export default Vue.extend({
  name: 'PreviewPoll',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return { tapIndex: 1 }
  },
  computed: {
    options(): Option[] {
      return [
        { label: 'Option A', pct: 18 },
        { label: 'Option B', pct: 54 },
        { label: 'Option C', pct: 22 },
        { label: 'Option D', pct: 6 },
      ]
    },
  },
})
</script>

<style scoped lang="scss">
.pl-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.pl-bar--xl { width: 70%; }
.pl-bar--md { width: 40%; }

.pl-question {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}
.pl-question .pl-bar:first-child { background: rgba(15, 15, 20, 0.2); }

.pl-results { display: grid; gap: 8px; }
.pl-row {
  display: grid;
  grid-template-columns: 64px 1fr auto;
  align-items: center;
  gap: 8px;
}
.pl-row-label {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.65);
}
.pl-track {
  height: 12px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.pl-fill {
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  animation: pl-grow 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--row-i, 0) * 90ms + 700ms);
}
.pl-pct {
  font-size: 10px; font-weight: 700;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
  min-width: 26px; text-align: right;
}

/* ---- Audience phone ---- */
.pl-phone-q {
  display: block;
  height: 8px; border-radius: 999px;
  width: 70%;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.pl-pill {
  appearance: none; background: white;
  display: flex; align-items: center; justify-content: space-between;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 15, 20, 0.08);
  cursor: pointer;
  font-family: inherit; font-size: 11px; font-weight: 700;
  color: rgba(15, 15, 20, 0.7);
  text-align: left;
}
.pl-pill.--tapped {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  color: var(--accent);
  animation: pl-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.pl-pill-check { font-size: 14px; color: var(--accent); }

@keyframes pl-grow {
  0%, 12% { width: 0; }
  60%     { width: var(--row-pct, 50%); }
  92%, 100% { width: var(--row-pct, 50%); }
}
@keyframes pl-tap {
  0%, 4%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%      { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%     { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .pl-fill { animation: none; width: var(--row-pct, 50%); }
  .pl-pill.--tapped { animation: none; }
}
</style>
